import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/services/adminApi';
import { ApiClientError } from '@/services/apiClient';
import type { LogDetail, LogRow } from '@/types/logs';

interface Props {
  row: LogRow;
  // Optional cached payload — when the parent already fetched once for this row,
  // pass it back so a re-expand renders instantly without re-fetching.
  cached?: LogDetail | null;
  onLoaded(id: string, detail: LogDetail): void;
}

export function LogRowDetail({ row, cached, onLoaded }: Props): JSX.Element {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<LogDetail | null>(cached ?? null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(cached === undefined || cached === null);

  useEffect(() => {
    if (cached) {
      setDetail(cached);
      setLoading(false);
      return;
    }
    const upstreamId = row.id.replace(/^(sentry|posthog):/, '');
    let cancelled = false;
    setLoading(true);
    void adminApi
      .getLogDetail(row.source, upstreamId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
        onLoaded(row.id, d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.status === 404) {
          setMissing(true);
        } else {
          setError(err instanceof Error ? err.message : t('admin.logs.detail.loadError'));
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [row.id, row.source, cached, onLoaded]);

  const vendorLabel = row.source === 'sentry' ? 'Sentry' : 'PostHog';

  return (
    <div className="admin-logs-detail">
      {loading && <p className="muted" role="status">{t('admin.logs.detail.loading')}</p>}
      {!loading && missing && (
        <p className="muted">{t('admin.logs.detail.missing')} <a href={row.permalink} target="_blank" rel="noreferrer">{t('admin.logs.detail.openIn', { vendor: vendorLabel })}</a></p>
      )}
      {!loading && error && (
        <p className="error" role="alert">{error}</p>
      )}
      {!loading && detail && (
        <>
          <div className="admin-logs-detail__actions">
            <a href={detail.permalink} target="_blank" rel="noreferrer">{t('admin.logs.detail.openIn', { vendor: vendorLabel })}</a>
          </div>
          <pre className="admin-logs-detail__raw">{JSON.stringify(detail.raw, null, 2)}</pre>
        </>
      )}
    </div>
  );
}
